import {
  useGetAccountManagersQuery,
  useGetPodsV2Query,
} from '@components/Users/slice/usersSlice';
import { useAppDispatch, useAppSelector } from '@utils/hooks';
import {
  GetCampaignsParams,
  useGetReportDatesV3Query as useGetReportDatesQueryV3,
} from '@features/campaigns';
import {
  pageDecremented,
  pageIncremented,
  pageSizeSelected,
  reportSelected,
} from './slice/approvalsSlice';
import {
  usePatchCampaignMutation,
  useGetCampaignsV2Query,
} from './slice/legacyAdminApi';
import { AdminReport } from './slice/schemas/legacyAdminSchema';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import { useGetCpgReportDatesQuery } from '@features/campaigns/subdomains/cpg/api/api';

const usePaging = () => {
  const dispatch = useAppDispatch();

  const loadNextPage = useCallback(() => {
    dispatch(pageIncremented());
  }, [dispatch]);

  const loadPreviousPage = useCallback(() => {
    dispatch(pageDecremented());
  }, [dispatch]);

  const changePageSize = useCallback(
    (pageSize: number) => {
      dispatch(pageSizeSelected(pageSize));
    },
    [dispatch]
  );

  return {
    loadNextPage,
    loadPreviousPage,
    changePageSize,
  };
};

const useCampaignsParams = () => {
  const selectedAccountManager = useAppSelector(
    state => state.approvals.selectedAccountManager
  );
  const selectedPod = useAppSelector(state => state.approvals.selectedPod);
  const selectedManagerFilter = useAppSelector(
    state => state.approvals.selectedManagerFilter
  );
  const searchText = useAppSelector(state => state.approvals.searchText);
  const selectedReportDate = useAppSelector(
    state => state.approvals.selectedReportDate
  );
  const currentPage = useAppSelector(state => state.approvals.currentPage);
  const showArchived = useAppSelector(state => state.approvals.showArchived);
  const pageSize = useAppSelector(state => state.approvals.pageSize);

  return {
    selectedAccountManager,
    selectedPod,
    selectedManagerFilter,
    searchText,
    selectedReportDate,
    currentPage,
    pageSize,
    showArchived,
  };
};

export const useLci3CampaignsParams = (): GetCampaignsParams => {
  const baseParams = useCampaignsParams();

  return {
    search_text: baseParams.searchText,
    page: baseParams.currentPage,
    per_page: baseParams.pageSize,
    show_archived: baseParams.showArchived,
    account_manager_id: baseParams.selectedAccountManager,
    pod_id: baseParams.selectedPod,
    manager_filter: baseParams.selectedManagerFilter,
    report_date: baseParams.selectedReportDate,
    include_unapproved: true,
  };
};

export const useApprovalsFilters = () => {
  const campaignParams = useLci3CampaignsParams();
  const { loadNextPage, loadPreviousPage, changePageSize } = usePaging();

  return {
    loadNextPage,
    loadPreviousPage,
    currentPage: campaignParams.page ?? 1,
    pageSize: campaignParams.per_page,
    changePageSize,
  };
};

export const useLegacyCampaigns = (skipRequest?: boolean) => {
  const {
    selectedAccountManager,
    selectedPod,
    selectedManagerFilter,
    searchText,
    selectedReportDate,
    currentPage,
    showArchived,
  } = useCampaignsParams();

  const campaignQueryParams = {
    user_id: selectedAccountManager,
    pod_id: selectedPod,
    manager_filter: selectedManagerFilter,
    search_text: searchText,
    report_date: selectedReportDate,
    page: currentPage,
    show_archived: showArchived,
  };

  return {
    query: useGetCampaignsV2Query(
      skipRequest ? skipToken : campaignQueryParams
    ),
    params: campaignQueryParams,
  };
};

export const useManagerQueries = () => {
  const podsQuery = useGetPodsV2Query();
  const accountManagersQuery = useGetAccountManagersQuery();

  return {
    podsQuery,
    accountManagersQuery,
  };
};

export const useApprovals = () => {
  const dispatch = useAppDispatch();
  const { currentPage, showArchived } = useCampaignsParams();
  const { loadNextPage, loadPreviousPage } = usePaging();

  const { query: campaignsQuery, params: campaignQueryParams } =
    useLegacyCampaigns();
  const { podsQuery, accountManagersQuery } = useManagerQueries();
  const legacyPatchCampaign = usePatchCampaignMutation();

  const checkedCampaigns = useAppSelector(
    state => state.approvals.checkedCampaigns
  );
  const checkedReports = useAppSelector(
    state => state.approvals.checkedReports
  );

  const selectReport = (report: AdminReport) =>
    dispatch(reportSelected({ report }));

  return {
    campaignsQuery,
    podsQuery,
    accountManagersQuery,
    checkedCampaigns,
    checkedReports,
    selectReport,
    legacyPatchCampaign,
    loadNextPage,
    loadPreviousPage,
    currentPage,
    showArchived,
    campaignQueryParams,
  };
};

type UseReportDatesHook = {
  data: string[] | undefined;
  isFetching: boolean;
  error: unknown;
};

export const useReportDates = (): UseReportDatesHook => {
  const campaignType = useAppSelector(state => state.approvals.campaignType);

  const {
    data: lci3Data,
    isFetching: lci3Loading,
    error: lci3Error,
  } = useGetReportDatesQueryV3(campaignType === 'lci3' ? undefined : skipToken);

  const {
    data: cpgData,
    isFetching: cpgLoading,
    error: cpgError,
  } = useGetCpgReportDatesQuery(campaignType === 'cpg' ? undefined : skipToken);

  return (() => {
    switch (campaignType) {
      case 'lci3': {
        return {
          data: lci3Data,
          isFetching: lci3Loading,
          error: lci3Error,
        };
      }
      case 'cpg': {
        return {
          data: cpgData,
          isFetching: cpgLoading,
          error: cpgError,
        };
      }
      default: {
        return {
          data: undefined,
          isFetching: false,
          error: undefined,
        };
      }
    }
  })();
};
